// 흡입 소용돌이: 바닥 위에서 바깥에서 안쪽으로 감겨 들어가는 나선 띠 3줄. 띠가 안쪽으로 가늘어지며 밝아지고, 결이 안쪽으로 흘러 빨려 들어가는 느낌을 준다.
// 전체가 안쪽으로 돌고(회전 방향 = 나선이 감기는 방향의 반대라 띠가 중심 쪽으로 밀려 들어가 보인다), 수명 동안 반경이 18% 줄어든다. 안쪽 끝은 살짝 위로 말려 올라간다(깔때기).
// 쓰는 칸: length = 바깥 반경(m), from = 안쪽 끝 반경 비율(0.3 = 가운데 구멍), width = 띠 두께(m), sweep = 나선이 감기는 전체 각(rad, 부호 = 감기는 방향; 6~8이 한 바퀴 반 ~ 두 바퀴),
//          y = 바닥 높이, ahead = 중심을 앞으로 옮기는 거리, life·color·color2(안쪽 밝은 끝)·intensity·seed. 드로우콜 1.
// Blender 메시로 바꿀 때: 띠 한 줄 메시(u = 바깥 0 → 안쪽 1, v = 안쪽 가장자리 0 → 바깥 가장자리 1)를 3개 인스턴스로 돌려 쓴다.
import * as THREE from 'three/webgpu';
import { abs, clamp, float, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { stripLayout } from './layout';
import { fxMaterial } from './material';
import { clamp01 } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export const VORTEX_SEG = 28;
export const VORTEX_ARMS = 3;
/** 전체가 안쪽으로 도는 속도(rad/s) */
const SPIN = 2.4;
const LIFT = 0.05;

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const env = ctl.y, seed = ctl.z;
  const t = t2.w;
  const u = uv().x, v = uv().y;
  const band = abs(v.mul(2).sub(1));
  const across = band.oneMinus().pow(1.3);
  // 결: 안쪽(u↑)으로 흐른다
  const n = texture(noise, vec2(u.mul(2.5).sub(t.mul(1.7)), v.mul(0.7).add(seed))).r;
  const n2 = texture(noise, vec2(u.mul(5.5).sub(t.mul(2.6)).add(0.31), v.mul(1.1).add(seed.mul(2)))).g;
  // 바깥 끝은 서서히 나타나고 안쪽으로 갈수록 밝다
  const ends = smoothstep(0.0, 0.14, u).mul(smoothstep(1.0, 0.94, u));
  const glow = u.pow(1.4);
  const grain = n.mul(0.6).add(n2.mul(0.3)).add(0.35);
  // c013: 몸통은 옅게(바닥 덮임 계약 COMBAT-FEEL 9.2 — 3m 원판을 칠하지 않는다), 읽힘은 가는 앞 가장자리(림)와 그 위를 안쪽으로 흐르는 속도 줄무늬가 맡는다.
  // 림 = 띠 바깥쪽(v 0.78~0.96)의 가는 선. 줄무늬 = 노이즈를 u 방향으로 길게 늘여 안쪽으로 빠르게 흘린 끊긴 선(정지 화면에서도 회전 방향이 보인다)
  const rimLine = smoothstep(0.5, 0.8, v).mul(float(1).sub(smoothstep(0.9, 1.0, v)));
  // 가는 심(림 안쪽 가운데): 흰 쪽(color2)으로 달아오르는 날카로운 선 — 값 대비로 따뜻한 돌바닥 위에서도 읽힌다
  const core = smoothstep(0.7, 0.8, v).mul(float(1).sub(smoothstep(0.84, 0.93, v)));
  const streak = smoothstep(0.42, 0.7, texture(noise, vec2(u.mul(1.6).sub(t.mul(3.2)), v.mul(0.15).add(seed.mul(3)))).b);
  const lit = streak.mul(0.7).add(0.3).mul(glow.mul(0.55).add(0.45));
  const rim = rimLine.mul(lit).mul(0.75).add(core.mul(lit));
  const body = across.mul(grain).mul(glow.mul(0.5).add(0.2)).mul(0.3);
  const alpha = clamp(body.add(rim), 0, 1).mul(ends).mul(env);
  const hot = clamp(core.mul(streak).mul(1.4).add(rimLine.mul(0.25)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, hot).mul(t1.w);
  return fxMaterial(col, alpha);
}

export class VortexModule extends BatchModule {
  readonly kind: ModuleKind = 'vortex';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.vortex) {
    super(cap, stripLayout(VORTEX_SEG, VORTEX_ARMS), makeMaterial(noise), 19, 'fx-vortex');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const age01 = clamp01(s.age / s.life);
    const R = s.length * s.scale * (1 - 0.18 * age01);
    const inner = R * Math.min(0.95, Math.max(0.02, s.from));
    const W = s.width * s.scale;
    const dir = s.sweep < 0 ? -1 : 1;
    const spin = -dir * SPIN * s.age + s.yaw;
    const sin0 = Math.sin(s.yaw), cos0 = Math.cos(s.yaw);
    const cx = s.x + sin0 * s.ahead, cz = s.z + cos0 * s.ahead;
    const funnel = R * 0.12;
    const per = (VORTEX_SEG + 1) * 2;
    for (let a = 0; a < VORTEX_ARMS; a++) {
      const base = spin + (a * Math.PI * 2) / VORTEX_ARMS;
      for (let i = 0; i <= VORTEX_SEG; i++) {
        const f = i / VORTEX_SEG;
        const r = R + (inner - R) * f;
        const th = base + s.sweep * f;
        const sn = Math.sin(th), cs = Math.cos(th);
        // 띠 두께: 바깥에서 두껍고 안쪽으로 가늘어진다
        const hw = 0.5 * W * (0.4 + 0.6 * (1 - f));
        const y = s.y + LIFT + funnel * f * f;
        const k = o + a * per * 3 + i * 6;
        out[k] = cx + sn * (r - hw); out[k + 1] = y; out[k + 2] = cz + cs * (r - hw);
        out[k + 3] = cx + sn * (r + hw); out[k + 4] = y; out[k + 5] = cz + cs * (r + hw);
      }
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, s.age / 0.08) * (1 - clamp01((age01 - 0.75) / 0.25));
    out[2] = s.seed;
    out[3] = 0;
  }

  protected override tint2w(s: Slot): number { return s.age; }
}
